import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import type { MissingFieldsReport } from '../../shared/types';
import classes from '../../bidAnalysisTemplate.module.scss';

interface TemplateHeaderProps {
  productName: string;
  isSaving: boolean;
  isSubmittingForReview?: boolean;
  progress?: number;
  currentStatus?: string;
  onSave: () => void;
  onSubmit?: () => void;
  onStatusChange?: (newStatus: string) => void;
  isManagerReviewMode?: boolean;
  onRequestModification?: () => void;
  onApprove?: () => void;
  bidStatus?: string;
  lastModifiedDate?: string;
  missingFieldsReport?: MissingFieldsReport | null;
  onShowMissingFields?: () => void;
  templateType?: string;
  onRequestNavigation?: (path: string) => void;
}

export const TemplateHeader: React.FC<TemplateHeaderProps> = ({
  productName,
  isSaving,
  isSubmittingForReview = false,
  progress = 0,
  currentStatus = 'Draft',
  onSave,
  onSubmit,
  onStatusChange,
  isManagerReviewMode = false,
  onRequestModification,
  onApprove,
  bidStatus,
  lastModifiedDate,
  missingFieldsReport: _missingFieldsReport,
  onShowMissingFields: _onShowMissingFields,
  templateType: _templateType,
  onRequestNavigation,
}) => {
  const navigate = useNavigate();
  const handleBreadcrumbNav = (path: string) => {
    if (onRequestNavigation) {
      onRequestNavigation(path);
    } else {
      navigate(path);
    }
  };
  // Determine available status options based on current status
  const getStatusOptions = () => {
    const currentStatusLower = currentStatus.toLowerCase();
    
    // If status is "Closed", only allow "Draft" or "Closed"
    if (currentStatusLower === 'closed') {
      return [
        { label: 'Draft', value: 'Draft' },
        { label: 'Closed', value: 'Closed' },
      ];
    }
    
    // If status is "In Review", "Needs modification", or "Approved", 
    // don't allow status change via dropdown (should use workflow actions)
    if (currentStatusLower === 'in review' || 
        currentStatusLower === 'in-review' ||
        currentStatusLower === 'needs modification' ||
        currentStatusLower === 'needs-modification' ||
        currentStatusLower === 'approved') {
      // Return only current status (read-only)
      return [
        { label: currentStatus, value: currentStatus },
      ];
    }
    
    // For "Draft" status, allow changing to "Closed" or "Draft"
    return [
      { label: 'Draft', value: 'Draft' },
      { label: 'Closed', value: 'Closed' },
    ];
  };

  // Check if status dropdown should be disabled
  const isStatusEditable = () => {
    const currentStatusLower = currentStatus.toLowerCase();
    return currentStatusLower === 'draft' || currentStatusLower === 'closed';
  };

  const statusOptions = getStatusOptions();
  // Find the option that matches currentStatus, or default to first option
  const selectedStatusValue = statusOptions.find(opt => 
    opt.value.toLowerCase() === currentStatus.toLowerCase()
  )?.value || statusOptions[0]?.value || 'Draft';

  const handleStatusChange = (e: any) => {
    const newStatus = e.value;
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
  };

  return (
    <div className={classes.topHeader}>
      <div className={classes.headerLeft}>
        <span className={classes.breadcrumb}>
          <i className="pi pi-home"></i>
          <span 
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => handleBreadcrumbNav('/bdportal/bid-analysis/dashboard')}
          >
            Dashboard
          </span>
          <i className="pi pi-angle-right"></i>
          {isManagerReviewMode ? (
            <>
              <span 
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => handleBreadcrumbNav('/bdportal/bid-analysis/manager-review')}
              >
                Reviewer Dashboard
              </span>
              <i className="pi pi-angle-right"></i>
              <span>{productName}</span>
            </>
          ) : (
            <>
              <span 
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => handleBreadcrumbNav('/bdportal/bid-analysis/dashboard')}
              >
                Analysis
              </span>
            </>
          )}
        </span>
        <h1 className={classes.mainTitle}>{productName}</h1>
      </div>
      <div className={classes.headerRight}>
        <Dropdown
          value={selectedStatusValue}
          options={statusOptions}
          onChange={handleStatusChange}
          optionLabel="label"
          optionValue="value"
          className={classes.statusDropdown}
          disabled={!onStatusChange || !isStatusEditable()}
          panelClassName="statusDropdownPanel"
          placeholder="Select Status"
        />
        
        {/* Missing Fields Button - Only show for NC templates */}
        {/* {templateType === 'nc' && missingFieldsReport && onShowMissingFields && (
          <Button
            label="Missing Fields"
            icon="pi pi-list-check"
            className={classes.headerBtn}
            outlined
            severity="warning"
            onClick={onShowMissingFields}
            badge={missingFieldsReport.missingFields > 0 ? String(missingFieldsReport.missingFields) : undefined}
            badgeClassName="p-badge-danger"
            tooltip={
              missingFieldsReport.missingFields > 0 
                ? `${missingFieldsReport.missingFields} fields missing` 
                : 'All fields complete'
            }
            tooltipOptions={{ position: 'bottom' }}
          />
        )} */}
        
        <span className={classes.lastSaved}>
          {lastModifiedDate ? `Last saved: ${lastModifiedDate}` : 'Last saved: 2 minutes ago'}
        </span>
        <div className={classes.progressInfo}>
          <span>{progress}% Complete</span>
          <div className={classes.miniProgress}>
            <div style={{ width: `${progress}%` }}></div>
          </div>
        </div>
        {isManagerReviewMode ? (
          <>
            <Button
              label={isSaving ? "Saving..." : "Save"}
              icon={isSaving ? "pi pi-spin pi-spinner" : "pi pi-save"}
              className={classes.headerBtn}
              outlined
              disabled={isSaving}
              onClick={onSave}
            />
            <Button
              label="Request Modification"
              icon="pi pi-pencil"
              className={classes.headerBtn}
              outlined
              onClick={onRequestModification || (() => {})}
              disabled={bidStatus?.toLowerCase() === 'approved'}
            />
            <Button
              label="Approve"
              icon="pi pi-check"
              className={classes.headerBtnPrimary}
              onClick={onApprove || (() => {})}
              disabled={bidStatus?.toLowerCase() === 'approved'}
            />
          </>
        ) : (
          <>
            <Button
              label={isSaving ? "Saving..." : "Save"}
              icon={isSaving ? "pi pi-spin pi-spinner" : "pi pi-save"}
              className={classes.headerBtn}
              outlined
              disabled={isSaving}
              onClick={onSave}
            />
            {currentStatus?.toLowerCase() !== 'approved' && bidStatus?.toLowerCase() !== 'approved' && (
              <Button
                label={isSubmittingForReview ? "Submitting..." : "Submit for Review"}
                icon={isSubmittingForReview ? "pi pi-spin pi-spinner" : "pi pi-send"}
                className={classes.headerBtnPrimary}
                onClick={onSubmit || (() => {})}
                disabled={currentStatus?.toLowerCase() === 'closed' || isSubmittingForReview || isSaving}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

